import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Camera, Upload, X, Search, Check } from 'lucide-react';
import { usePatientList, useVisitTypes, useSendCapturePhoto, usePatientProfile } from '../hooks/useApi';
import { useDebounce } from '../hooks/useDebounce';


import * as faceapi from 'face-api.js';

const PhotoCapture = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [formData, setFormData] = useState({
    patientId: searchParams.get('patientId') || '',
    visit_type_id: '',
    notes: '',
    customTags: [],
    tagInput: '',
    treatmentPlan: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cameraMode, setCameraMode] = useState('upload'); // 'camera' or 'upload'
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientSelectRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const [deviceSource, setDeviceSource] = useState(null); // 'camera' | 'upload'
  const [isMasking, setIsMasking] = useState(false);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [isMasked, setIsMasked] = useState(false);


  // Hooks - must be declared before useEffect that uses them
  const debouncedSearch = useDebounce(search, 500);
  const { data: patientListData, isLoading: isLoadingPatientList } =
    usePatientList(debouncedSearch, {
      enabled: isSearching && debouncedSearch.length > 0,
    });

  const patientList = patientListData?.data || [];
  const { data: visitTypesData, isLoading: isLoadingVisitTypes } = useVisitTypes();
  const { mutate: sendCapturePhoto, isPending: isSubmitting } = useSendCapturePhoto();
  const visitTypes = visitTypesData?.data || [];

  // Fetch initial patient data if patientId is present in URL
  const patientIdFromParams = searchParams.get('patientId');
  const { data: initialPatientData } = usePatientProfile(patientIdFromParams, {
    enabled: !!patientIdFromParams
  });

  useEffect(() => {
    if (initialPatientData?.data) {
      setSearch(initialPatientData.data.full_name);
      // Ensure formData has the correct patientId (though it should be initialized already)
      setFormData(prev => ({
        ...prev,
        patientId: initialPatientData.data.id
      }));
    }
  }, [initialPatientData]);



  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (patientSelectRef.current && !patientSelectRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };

    if (showPatientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPatientDropdown]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsCameraActive(true);


      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Please allow camera access');
      setCameraMode('upload');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setIsCameraActive(false);
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Preserve native camera resolution
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.drawImage(video, 0, 0);

    // 1️⃣ Get raw image (LOSSLESS)
    const rawImage = canvas.toDataURL('image/png');

    // 2️⃣ Convert to blob directly without masking
    const rawBlob = await fetch(rawImage).then(res => res.blob());

    // 3️⃣ Create file
    const file = new File(
      [rawBlob],
      `photo-${Date.now()}.png`,
      { type: 'image/png' }
    );

    setSelectedFile(file);
    setPreviewUrl(rawImage); // Show raw image
    setOriginalUrl(rawImage);
    setIsMasked(false);
    setDeviceSource('camera');

    stopCamera();
  };


  const handleCameraModeChange = (mode) => {
    if (mode === cameraMode) return;
    if (mode === 'camera') startCamera();
    else stopCamera();
    setCameraMode(mode);
  };






  const maskEyes = async (imgUrl) => {
    if (!modelsLoaded) {
      console.warn('Models not loaded yet, returning original image');
      return imgUrl;
    }

    console.log('Masking eyes for image:', imgUrl.slice(0, 50), '...');
    const img = new Image();
    img.src = imgUrl;
    await img.decode();

    const detection = await faceapi
      .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.5 }))
      .withFaceLandmarks();

    console.log('Detection result:', detection);

    if (!detection) {
      console.warn('No face detected');
      return imgUrl;
    }

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const landmarks = detection.landmarks;
    console.log('Landmarks:', landmarks);

    if (!landmarks) return imgUrl;

    const drawMask = (points) => {
      const xs = points.map(p => p.x);
      const ys = points.map(p => p.y);
      const x = Math.min(...xs) - 5;
      const y = Math.min(...ys) - 5;
      const width = Math.max(...xs) - x + 10;
      const height = Math.max(...ys) - y + 10;
      ctx.fillStyle = 'black';
      ctx.fillRect(x, y, width, height);
      console.log('Mask drawn at:', { x, y, width, height });
    };

    drawMask(landmarks.getLeftEye());
    drawMask(landmarks.getRightEye());

    return canvas.toDataURL('image/png');
  };

  const handleManualMask = async () => {
    if (!previewUrl) return;

    try {
      setIsMasking(true);
      // Use originalUrl if available to ensure clean masking, otherwise previewUrl
      const sourceUrl = originalUrl || previewUrl;
      const maskedImage = await maskEyes(sourceUrl);

      if (maskedImage === sourceUrl) {
        toast.info("No face detected or models not loaded.");
      } else {
        toast.success("Image masked successfully.");
        setIsMasked(true);
      }

      // Convert to blob and update file
      const maskedBlob = await fetch(maskedImage).then(res => res.blob());
      const maskedFile = new File(
        [maskedBlob],
        selectedFile?.name.replace(/\.\w+$/, '-masked.png') || `masked-${Date.now()}.png`,
        { type: 'image/png' }
      );

      setSelectedFile(maskedFile);
      setPreviewUrl(maskedImage);

    } catch (error) {
      console.error("Manual masking error:", error);
      toast.error("Failed to mask image.");
    } finally {
      setIsMasking(false);
    }
  };

  const handleUnmask = async () => {
    if (!originalUrl) return;

    try {
      // Restore original image
      const originalBlob = await fetch(originalUrl).then(res => res.blob());
      const originalFile = new File(
        [originalBlob],
        selectedFile?.name.replace('-masked', '') || `photo-${Date.now()}.png`,
        { type: 'image/png' }
      );

      setSelectedFile(originalFile);
      setPreviewUrl(originalUrl);
      setIsMasked(false);
      toast.success("Image unmasked.");
    } catch (error) {
      console.error("Unmasking error:", error);
      toast.error("Failed to unmask.");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Please select an image smaller than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      // Just load the image, don't mask yet
      const rawImage = reader.result;

      const rawFile = file; // already a file object

      setSelectedFile(rawFile);
      setPreviewUrl(rawImage);
      setOriginalUrl(rawImage);
      setIsMasked(false);
      setDeviceSource('upload');
    };

    reader.readAsDataURL(file);
  };


  const handleRemovePhoto = () => {
    console.log('Removing photo');
    setSelectedFile(null);
    setPreviewUrl(null);
    setOriginalUrl(null);
    setIsMasked(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (isCameraActive) stopCamera();
    setCameraMode('upload');
  };

  const handleSubmit = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    if (!formData.visit_type_id) {
      toast.error('Please select a visit type');
      return;
    }
    if (!selectedFile) {
      toast.error('Please capture or upload a photo');
      return;
    }

    try {
      // Find selected patient for tags
      let selectedPatient = patientList.find(p => String(p.id) === String(formData.patientId));

      // Fallback to initialPatientData if not found in list
      if (!selectedPatient && initialPatientData?.data && String(initialPatientData.data.id) === String(formData.patientId)) {
        selectedPatient = initialPatientData.data;
      }

      // Prepare tags array
      const tagsArray = [];
      if (selectedPatient?.diagnosis) {
        if (Array.isArray(selectedPatient.diagnosis)) {
          tagsArray.push(...selectedPatient.diagnosis);
        } else {
          tagsArray.push(selectedPatient.diagnosis);
        }
      }
      if (formData.customTags.length > 0) {
        tagsArray.push(...formData.customTags);
      }


      // Format captured_at as "YYYY-MM-DD HH:mm:ss"
      const now = new Date();

      const pad = (n) => String(n).padStart(2, '0');

      const captured_at = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
        now.getSeconds()
      )}`;

      // Create FormData
      const formDataPayload = new FormData();

      // Append photo file
      formDataPayload.append('photo', selectedFile, selectedFile.name);

      // Append visit_type_id
      formDataPayload.append('visit_type_id', formData.visit_type_id);

      // Append device source
      formDataPayload.append('device_source', deviceSource);


      // Append tags as array
      tagsArray.forEach(tag => {
        formDataPayload.append('tags[]', tag);
      });

      // Append notes
      if (formData.notes) {
        formDataPayload.append('notes', formData.notes);
      }



      // Append captured_at
      formDataPayload.append('captured_at', captured_at);

      // Append treatment_plan
      if (formData.treatmentPlan) {
        formDataPayload.append('treatment_plan', formData.treatmentPlan);
      }

      // Submit to API
      sendCapturePhoto(
        {
          patientId: formData.patientId,
          formData: formDataPayload
        },
        {
          onSuccess: (data) => {
            toast.success(`Photo saved successfully`);
            navigate(`/patients/${formData.patientId}`);
          },
          onError: (error) => {
            console.error('Photo capture error:', error);
            toast.error(error.response?.data?.message || 'Failed to save photo. Please try again.');
          }
        }
      );
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error('Failed to save photo. Please try again.');
    }
  };

  useEffect(() => {
    const loadModels = async () => {
      try {
        console.log('Loading face-api models from local /models folder...');

        const modelPath = '/admin/';

        // Load models from local public/models folder
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(modelPath),
          faceapi.nets.faceLandmark68Net.loadFromUri(modelPath)
        ]);

        setModelsLoaded(true);
        console.log('Models loaded successfully from local folder');
      } catch (err) {
        console.error('Model loading failed:', err);
        toast.error(`Unable to load face detection models. ${err.message}. Please ensure faceLandmark68Net model files are in /public/models folder.`);
      }
    };
    loadModels();
  }, [toast]);

  const addTag = (value) => {
    const tag = value.trim();
    if (!tag) return;

    setFormData(prev => {
      if (prev.customTags.includes(tag)) return prev; // avoid duplicates
      return {
        ...prev,
        customTags: [...prev.customTags, tag],
        tagInput: '',
      };
    });
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      customTags: prev.customTags.filter(tag => tag !== tagToRemove),
    }));
  };


  return (
    <div className="max-w-6xl mx-auto space-y-3 animate-fadeIn">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Capture Photo</h1>
        <p className="text-gray-600 mt-1">Take or upload patient photos</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Photo Details Card */}
        <Card>
          <CardHeader>
            <CardTitle>Photo Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className=' grid grid-cols-2 gap-5 '>

              <div className="space-y-2 relative">
                <Label htmlFor="patient">Select Patient <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="patient-search"
                    placeholder="Search for a patient"
                    value={search}
                    onFocus={() => setIsSearching(true)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearch(value);

                      // 🔴 clear selected patient when input is empty
                      if (!value.trim()) {
                        setFormData((prev) => ({ ...prev, patientId: '' }));
                        setShowPatientDropdown(false);
                        setIsSearching(false);
                        return;
                      }

                      setShowPatientDropdown(true);
                      setIsSearching(true);
                    }}
                    className="pl-10"
                  />


                  {formData.patientId && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                </div>

                {/* Patient Dropdown */}
                {showPatientDropdown && (
                  <div
                    ref={patientSelectRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    {isLoadingPatientList ? (
                      <div className="p-4 text-center text-gray-500">Loading patients...</div>
                    ) : patientList.length > 0 ? (
                      patientList.map((patient) => (
                        <div
                          key={patient.id}
                          onClick={() => {
                            setFormData({ ...formData, patientId: String(patient.id) });
                            setSearch(patient.full_name || patient.fullName || '');
                            setShowPatientDropdown(false);
                            setIsSearching(false);
                          }}

                          className={`p-3 cursor-pointer hover:bg-gray-100 transition-colors ${String(formData.patientId) === String(patient.id) ? 'bg-primary-50' : ''
                            }`}
                        >
                          <div className="font-medium text-gray-900">{patient.full_name || 'N/A'}</div>
                          {patient.patient_uid && (
                            <div className="text-xs text-gray-500">ID: {patient.patient_uid}</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        {search ? 'No patients found' : 'Start typing to search'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="visitType">Visit Type <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.visit_type_id}
                  onValueChange={(value) => setFormData({ ...formData, visit_type_id: value })}
                  disabled={isLoadingVisitTypes}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isLoadingVisitTypes ? "Loading..." : "Select visit type"} />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingVisitTypes ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : visitTypes.length > 0 ? (
                      visitTypes.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>{type.name}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-types" disabled>No visit types available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

            </div>
            <div className="space-y-2">
              <Label>Custom Tags</Label>

              {/* Tags container */}
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.customTags.map((tag, index) => (
                  <span
                    key={index}
                    className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:text-red-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              {/* Tag input */}
              <Input
                placeholder="Type tag and press comma or Enter"
                value={formData.tagInput}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, tagInput: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === ',' || e.key === 'Enter') {
                    e.preventDefault();
                    addTag(formData.tagInput);
                  }
                }}
                onBlur={() => {
                  // Create tag when user clicks outside
                  addTag(formData.tagInput);
                }}
              />

              <p className="text-xs text-gray-500">
                Press comma, enter, or click outside to add a tag
              </p>
            </div>


            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" placeholder="Add any notes about this photo" value={formData.notes} onKeyPress={(e) => {
                if (!/^[a-zA-Z0-9,./\- ]$/.test(e.key)) {
                  e.preventDefault();
                }
              }} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} />
            </div>

            {/* <div className="space-y-2">
              <Label htmlFor="medicalHistory">Medical History</Label>
              <Textarea 
                id="medicalHistory" 
                placeholder="Enter patient's medical history" 
                value={formData.medicalHistory} 
                onChange={(e) => setFormData({ ...formData, medicalHistory: e.target.value })} 
                rows={4} 
              />
            </div> */}

            <div className="space-y-2">
              <Label htmlFor="treatmentPlan">Treatment Plan</Label>
              <Textarea
                id="treatmentPlan"
                placeholder="Enter treatment plan details"
                value={formData.treatmentPlan}
                onKeyPress={(e) => {
                  if (!/^[a-zA-Z0-9,./\- ]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => setFormData({ ...formData, treatmentPlan: e.target.value })}
                rows={4}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => navigate(-1)} className="flex-1" disabled={isSubmitting}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                disabled={isSubmitting || !formData.patientId || !formData.visit_type_id || !selectedFile}
              >
                {isSubmitting ? 'Saving...' : 'Save Photo'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Photo Capture Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-4">
              <Button
                variant={cameraMode === 'camera' ? 'default' : 'ghost'}
                onClick={() => handleCameraModeChange('camera')}
              >
                <Camera className="w-5 h-5 mr-2" /> Camera
              </Button>
              <Button
                variant={cameraMode === 'upload' ? 'default' : 'ghost'}
                onClick={() => handleCameraModeChange('upload')}
              >
                <Upload className="w-5 h-5 mr-2" /> Upload
              </Button>
              <div>
                <Button
                  onClick={isMasked ? handleUnmask : handleManualMask}
                  disabled={!previewUrl || isMasking || !modelsLoaded}
                  variant="outline"
                >
                  {isMasking ? 'Processing...' : (isMasked ? 'Unmask Image' : 'Mask Image')}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {previewUrl ? (
              <div className="relative">
                <img src={previewUrl} alt="Preview" className="w-full h-fit object-cover rounded-lg" />
                <Button variant="destructive" size="icon" className="absolute top-2 right-2" onClick={handleRemovePhoto}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : cameraMode === 'camera' ? (
              <div className="relative">
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-black">
                  <video ref={videoRef} autoPlay playsInline className="w-full h-48 sm:h-64 md:h-80 object-cover" />
                  {!isCameraActive && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                      <div className="text-center text-white">
                        <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-gray-400">Starting camera...</p>
                      </div>
                    </div>
                  )}
                </div>
                <canvas ref={canvasRef} className="hidden" />
                <div className="mt-4 flex gap-3">
                  <Button variant="outline" onClick={() => { stopCamera(); setCameraMode('upload'); }} className="flex-1">Cancel</Button>
                  <Button onClick={capturePhoto} disabled={!isCameraActive} className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white">
                    <Camera className="w-4 h-4 mr-2" /> Capture Photo
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="border-2 h-80 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-primary-500 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                  <p className="text-sm text-gray-500">PNG, JPG or WEBP (MAX. 10MB)</p>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
                <Button onClick={() => fileInputRef.current?.click()} className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white">
                  <Upload className="w-4 h-4 mr-2" /> Upload Photo
                </Button>
              </>
            )}
          </CardContent>
        </Card>


      </div>
    </div>
  );
};

export default PhotoCapture;
